// The Extensions Invoice, for the optional extensions to a Life Study (the
// documentary, the sonic archive, the digital vault, or bespoke additions).
// Each extension is quoted separately, so this is a simple multi-line invoice
// with a single total, kept apart from the four-instalment Life Study invoice.
// Built from the shared documentKit for brand consistency.

import {
  escapeHtml,
  fillField,
  buildMasthead,
  buildSignatureBlock,
  buildFooter,
  wrapDocument,
} from "./documentKit";
import { EXTENSION_PRICES } from "./extensionPricing";

const fmt = (n) => n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const money = (n) => `R ${fmt(n)}`;
const parseMoney = (s) => {
  const n = parseFloat(String(s || "").replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
};

// Standard (house) pricing for the extensions lives in ./extensionPricing so the
// Extensions Prospectus and this invoice can never disagree. Prefilled when a
// preset line is added; every amount stays fully editable per invoice. The
// documentary is a "from" figure, confirmed on scoping, so it prefills as a floor.
export const EXT_PRESETS = {
  documentary: {
    name: "The Documentary",
    desc: "A broadcast-grade film drawn from the residency, edited and delivered as a finished documentary of the life.",
    amount: EXTENSION_PRICES.documentary,
  },
  sonic: {
    name: "The Sonic Archive",
    desc: "A recording of the voice and the rooms it lived in, mastered and preserved as a lasting sonic archive.",
    amount: EXTENSION_PRICES.sonic,
  },
  vault: {
    name: "The Digital Vault",
    desc: "A secured digital archive holding the entire research corpus, the papers, the interviews, and the photographs, for the generations to come.",
    amount: EXTENSION_PRICES.vault,
  },
  custom: { name: "", desc: "", amount: "" },
};

const EXT_INVOICE_CSS = `
.metablock{text-align:center;margin:-8px 0 6px;}
.metablock .meta{color:var(--slate);font-size:13px;margin:2px 0;}
.invtitle{text-align:center;font-style:italic;font-size:30px;letter-spacing:0.14em;margin:20px 0 4px;}
.invsub{text-align:center;font-style:italic;color:var(--slate);font-size:15px;margin-bottom:24px;}
.billing{display:flex;justify-content:space-between;gap:24px;margin-bottom:14px;font-size:13.5px;}
.billing .k{font-style:italic;color:var(--slate);}
.billing .right{text-align:right;}
.billing .colhead{font-variant:small-caps;letter-spacing:0.22em;color:var(--ox);font-size:11px;margin-bottom:5px;}
.linetable td .desc{color:var(--slate);font-style:italic;font-size:12px;display:block;margin-top:3px;}
.linetable td .title{color:var(--ox);}
.totes{width:100%;font-size:14px;margin-top:4px;}
.totes td{border:none;padding:5px 10px;}
.totes .lab{text-align:right;}
.totes .val{text-align:right;width:34%;}
.totes .grand .lab,.totes .grand .val{color:var(--ox);font-size:17px;padding-top:8px;}
.label{font-variant:small-caps;letter-spacing:0.28em;font-size:12px;color:var(--ox);margin:20px 0 6px;}
.pay p{font-size:13.5px;margin:3px 0;}
.notes p{font-size:13px;margin:6px 0;}
.sig .nm{font-style:italic;color:var(--ox);font-size:18px;}
`;

export function buildExtensionsInvoiceHtml(fields = {}) {
  const {
    billedTo,
    billedCity,
    invoiceNumber,
    invoiceDate,
    dueDate,
    projectTitle,
    agreementDate,
    items,
    depositNote,
  } = fields;

  const list = items && items.length ? items : [{ name: "", desc: "", amount: "" }];
  const rows = list
    .map((it) => {
      const amt = (it.amount || "").trim();
      const descHtml =
        it.desc && it.desc.trim() ? `<span class="desc">${escapeHtml(it.desc.trim())}</span>` : "";
      return `<tr><td><span class="title">${fillField(
        it.name,
        "extension"
      )}</span>${descHtml}</td><td class="r strongcell">${
        amt ? fmt(parseMoney(amt)) : '<span class="fill">[ amount ]</span>'
      }</td></tr>`;
    })
    .join("");
  const subtotal = list.reduce((s, it) => s + parseMoney(it.amount), 0);

  const depositHtml =
    depositNote && depositNote.trim()
      ? `<p>${escapeHtml(depositNote.trim())}</p>`
      : "";

  const body = `${buildMasthead()}<div class="metablock"><div class="meta">The Raven&#8217;s Quill (Pty) Ltd &#183; Pretoria, South Africa</div><div class="meta">info@theravensquill.co.za &#183; +27 79 281 8435</div><div class="meta">Company registration no.: 2025/875945/07</div></div><div class="invtitle">I N V O I C E</div><div class="invsub">The Life Study, Extensions</div><div class="billing"><div><div class="colhead">Billed to</div><div>${fillField(
    billedTo,
    "Subject&#x27;s full legal name"
  )}</div><div>${fillField(
    billedCity,
    "Subject&#x27;s city, province"
  )}</div></div><div class="right"><div><span class="k">Invoice number:</span> ${fillField(
    invoiceNumber,
    "RQ-EX-2026-001"
  )}</div><div><span class="k">Invoice date:</span> ${fillField(
    invoiceDate,
    "date"
  )}</div><div><span class="k">Due date:</span> ${fillField(
    dueDate,
    "date"
  )}</div><div><span class="k">Project:</span> ${fillField(
    projectTitle,
    "project title"
  )}</div><div><span class="k">Agreement dated:</span> ${fillField(
    agreementDate,
    "date"
  )}</div></div></div><table class="linetable"><tr><th>Description</th><th class="r">Amount (ZAR)</th></tr>${rows}</table><table class="totes"><tr><td class="lab">Subtotal</td><td class="val">${money(
    subtotal
  )}</td></tr><tr><td class="lab">VAT</td><td class="val">Not applicable</td></tr><tr class="grand"><td class="lab">Total due</td><td class="val">${money(
    subtotal
  )}</td></tr></table><div class="label">Payment details</div><div class="pay"><p>Account name:&nbsp; The Raven&#8217;s Quill</p><p>Bank:&nbsp; First National Bank</p><p>Account number:&nbsp; 63182970137</p><p>Branch code:&nbsp; 250655</p><p>Reference:&nbsp; ${fillField(
    invoiceNumber,
    "RQ-EX-2026-001"
  )}</p></div><div class="label">Notes</div><div class="notes"><p>These are the optional extensions to the Life Study for ${fillField(
    projectTitle,
    "project title"
  )}, quoted separately from the commission fee as set out in the proposal. Each extension is shaped to the life and priced on its own; this invoice is additional to, and independent of, the Life Study instalments.</p>${depositHtml}<p>The Raven&#8217;s Quill (Pty) Ltd is not registered for VAT; no value-added tax is charged on this invoice. Payment by electronic funds transfer to the account above, using the reference provided. Travel, where an extension requires it, is reconciled separately against receipts.</p></div>${buildSignatureBlock()}${buildFooter({ fline: "THE RAVEN&#8217;S QUILL &#183; THE LIFE STUDY, EXTENSIONS" })}`;

  return wrapDocument({ title: "The Life Study, Extensions Invoice", extraCss: EXT_INVOICE_CSS, body });
}
